import type { RequestConfig } from "@app/config/axios/http";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import { divisionApi, divisionServices, type ApiDivision } from "@src/api/dictionaries/divisions";
import { measureApi, type ApiMeasure } from "@src/api/dictionaries/measures";
import { ordersApi, type ApiOrder } from "@src/api/orders";
import { stockDepotCardsApi, type ApiStockDepotCard } from "@src/api/stock/stockDepotCards";
import { stockObjsApi, type ApiStockObj, type StockObj } from "@src/api/stock/stockObjs";
import { lotsServices, type ApiStockLot } from "../lots";
import { stockInplantsServices } from "../stockInplants";
import { stockOptionsServices } from "../stockOptions";
import { storePlacesApi, storePlacesServices, type ApiSpMovingData, type StorePlaceRemainder } from "../storePlaces";
import { stockGatheringsApi } from "./api.core";

import type {
    ApiStockGatheringContentData,
    ApiStockGatheringData,
    ApiStockReserveData,
    StockGathering,
    StockGatheringCompleteReserveParams,
    StockGatheringContent,
    StockGatheringContentExtends,
    StockGatheringContentFilter,
    StockGatheringExtends,
    StockGatheringFilter,
    StockGatheringFinishComplectParams,
    StockGatheringFinishComplectResult,
    StockGatheringReserveItem,
    StockReserve,
    StockReserveExtends,
    StockReserveFilter,
} from "./types";

/** Сервисный слой — КВ, состав КВ и резервы комплектования. */
export const stockGatheringsServices = {
    ...stockGatheringsApi,

    /**
     * Временная оркестрация завершения комплектования по КВ (#84798 / #86395). \
     * 1) `"stock/options.load"` → целевой статус `PDT_Freor.StockInplStateToPromoteAfterComplect`; \
     * 2) `"stock/stockInplants.loadByFilter"` по `GatheringCodes`; \
     * 3) для каждой ВЗН — `bo.avOperations` и `bo.performOperation` с `NextState` = целевой статус. \
     * TODO(api): заменить одним серверным действием завершения комплектования по КВ.
     * @param params код КВ
     * @param config опции HTTP-запроса
     */
    finishComplect: async (
        params: StockGatheringFinishComplectParams,
        config?: RequestConfig,
    ): Promise<StockGatheringFinishComplectResult> => {
        const options = await stockOptionsServices.load(config);
        const targetStateCode = options.PDT_Freor.StockInplStateToPromoteAfterComplect;

        if (!isValidCode(targetStateCode)) {
            throw new Error("Не задан статус ВЗН СУ для перевода после комплектования (Общие параметры СУ → вкладка ТСД).");
        }

        const inplants = await stockInplantsServices.load(
            {
                flt: { GatheringCodes: [params.gatheringCode] },
            },
            config,
        );

        let promotedCount = 0;
        let skippedCount = 0;

        for (const inplant of inplants) {
            if (inplant.bo.State === targetStateCode) {
                skippedCount += 1;
                continue;
            }

            const boCode = isValidCode(inplant.bo.BoCode) ? inplant.bo.BoCode : inplant.Code;
            const operations = await boApi.operations.loadAvailable({ boCode }, config);
            const promoteOperation = operations.find((operation) => operation.NextState === targetStateCode);

            if (promoteOperation == null) {
                throw new Error(
                    `Для ВЗН СУ №${inplant.Num} нет доступной BO-операции в статус из общих параметров СУ (код ${targetStateCode}).`,
                );
            }

            await boApi.operations.perform(promoteOperation.Code, boCode, config);
            promotedCount += 1;
        }

        return {
            inplantCount: inplants.length,
            promotedCount,
            skippedCount,
        };
    },

    /**
     * Загружает КВ и при необходимости догружает отправителя, получателя и состояние BO.
     * @param params фильтр и include
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            flt?: StockGatheringFilter;
            include?: Array<keyof StockGatheringExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockGathering[]> => {
        const { flt, include } = params;
        const gatherings = await stockGatheringsApi.load({ flt }, config);

        if (include == null) {
            return gatherings.map((gathering) => serializeGathering(gathering));
        }

        const includeSenderDivision = include.includes("SenderDivision");
        const includeSenderSection = include.includes("SenderSectionData");
        const includeReceiverDivision = include.includes("ReceiverDivision");
        const includeReceiverSection = include.includes("ReceiverSectionData");
        const includeState = include.includes("State");
        const divisionCodes = new Set<number>();
        const sectionCodes = new Set<number>();
        const stateCodes = new Set<number>();

        gatherings.forEach((gathering) => {
            if (includeSenderDivision) addValidCode(divisionCodes, gathering.Sender);
            if (includeReceiverDivision) addValidCode(divisionCodes, gathering.Receiver);
            if (includeSenderSection) addValidCode(sectionCodes, gathering.SenderSection);
            if (includeReceiverSection) addValidCode(sectionCodes, gathering.ReceiverSection);
            if (includeState) addValidCode(stateCodes, gathering.bo.State);
        });

        const divisions = new Map<number, ApiDivision>();
        const sections = new Map<number, ApiDivision>();
        const states = new Map<number, ApiOmpBoState>();
        const promises: Promise<unknown>[] = [];

        if (divisionCodes.size > 0) {
            promises.push(
                divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config).then(({ items }) => {
                    items.forEach((item) => divisions.set(item.Code, item));
                }),
            );
        }

        if (sectionCodes.size > 0) {
            promises.push(
                divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config).then((items) => {
                    items.forEach((item) => {
                        if (item.SectionCode !== 0) {
                            sections.set(item.SectionCode, item);
                        }
                    });
                }),
            );
        }

        if (stateCodes.size > 0) {
            promises.push(
                boApi.states.load({ Codes: [...stateCodes] }, config).then((items) => {
                    items.forEach((item) => states.set(item.Code, item));
                }),
            );
        }

        await Promise.all(promises);

        return gatherings.map((gathering) =>
            serializeGathering(gathering, {
                SenderDivision: includeSenderDivision ? divisions.get(gathering.Sender) : undefined,
                SenderSectionData: includeSenderSection ? sections.get(gathering.SenderSection) : undefined,
                ReceiverDivision: includeReceiverDivision ? divisions.get(gathering.Receiver) : undefined,
                ReceiverSectionData: includeReceiverSection ? sections.get(gathering.ReceiverSection) : undefined,
                State: includeState ? states.get(gathering.bo.State) : undefined,
            }),
        );
    },

    /**
     * Возвращает одну КВ по коду.
     * @param params код КВ и include
     * @param config опции HTTP-запроса
     */
    loadByCode: async (
        params: {
            code: number;
            include?: Array<keyof StockGatheringExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockGathering | null> => {
        const gatherings = await stockGatheringsServices.load(
            {
                flt: { Codes: [params.code] },
                include: params.include,
            },
            config,
        );
        return gatherings[0] ?? null;
    },

    /** Состав КВ. */
    content: {
        /**
         * Загружает строки состава КВ и связанные справочники.
         * @param params фильтр и include состава
         * @param config опции HTTP-запроса
         */
        load: async (
            params: {
                flt?: StockGatheringContentFilter;
                include?: Array<keyof StockGatheringContentExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockGatheringContent[]> => {
            const { flt, include } = params;
            const contents = await stockGatheringsApi.content.load({ flt }, config);

            if (include == null) {
                return contents.map((content) => serializeContent(content));
            }

            return enrichContents(contents, include, config);
        },
    },

    /** Резервы строк состава КВ. */
    reserves: {
        /**
         * Загружает резервы и связанные справочники.
         * @param params фильтр и include резервов
         * @param config опции HTTP-запроса
         */
        load: async (
            params: {
                flt?: StockReserveFilter;
                include?: Array<keyof StockReserveExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockReserve[]> => {
            const { flt, include } = params;
            const { Codes: reserveCodes, ...apiFlt } = flt ?? {};

            // `Codes` пока client-only: backend `stock/reserves.loadByFilter` их не принимает.
            // Сначала грузим по поддерживаемым полям, затем сужаем выборку до нужных кодов,
            // чтобы не enrich-ить лишние резервы. TODO(api): убрать client-side filter после доработки API.
            let reserves = await stockGatheringsApi.reserves.load(
                { flt: Object.keys(apiFlt).length > 0 ? apiFlt : undefined },
                config,
            );

            if (reserveCodes != null && reserveCodes.length > 0) {
                const reserveCodeSet = new Set(reserveCodes);
                reserves = reserves.filter((reserve) => reserveCodeSet.has(reserve.Code));
            }

            if (include == null) {
                return reserves.map((reserve) => serializeReserve(reserve));
            }

            return enrichReserves(reserves, include, config);
        },

        /**
         * Загружает один резерв строки состава КВ с связанной строкой состава.
         * @param params код резерва, код строки состава, include
         * @param config опции HTTP-запроса
         */
        loadOneForContent: async (
            params: {
                reserveCode: number;
                gatheringContCode: number;
                contentInclude?: Array<keyof StockGatheringContentExtends>;
                reserveInclude?: Array<keyof StockReserveExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockGatheringReserveItem | null> => {
            const [contents, reserves] = await Promise.all([
                stockGatheringsServices.content.load(
                    {
                        flt: { Code: params.gatheringContCode },
                        include: params.contentInclude,
                    },
                    config,
                ),
                stockGatheringsServices.reserves.load(
                    {
                        flt: {
                            GatheringContCodes: [params.gatheringContCode],
                            // Client-side until API supports Codes — see StockReserveFilter.Codes.
                            Codes: [params.reserveCode],
                        },
                        include: params.reserveInclude,
                    },
                    config,
                ),
            ]);

            const content = contents[0];
            const reserve = reserves[0];

            if (reserve == null) {
                return null;
            }

            return {
                ...reserve,
                Content: content,
            };
        },

        /**
         * Загружает резервы по составу КВ, не смешивая запрос шапки карточки с запросом резервов.
         * @param params код КВ и include для состава/резервов
         * @param config опции HTTP-запроса
         */
        loadForContents: async (
            params: {
                gatheringCode: number;
                contentInclude?: Array<keyof StockGatheringContentExtends>;
                reserveInclude?: Array<keyof StockReserveExtends>;
            },
            config?: RequestConfig,
        ): Promise<StockGatheringReserveItem[]> => {
            const contents = await stockGatheringsServices.content.load(
                {
                    flt: { GatheringCode: params.gatheringCode },
                    include: params.contentInclude,
                },
                config,
            );
            const contentCodes = contents.map((content) => content.Code).filter(isValidCode);

            if (contentCodes.length === 0) {
                return [];
            }

            const reserves = await stockGatheringsServices.reserves.load(
                {
                    flt: { GatheringContCodes: contentCodes },
                    include: params.reserveInclude,
                },
                config,
            );
            const contentsByCode = new Map<number, StockGatheringContent>();
            contents.forEach((content) => contentsByCode.set(content.Code, content));

            return reserves.map((reserve) => ({
                ...reserve,
                Content: contentsByCode.get(reserve.GatheringContCode),
            }));
        },

        /**
         * Создает отпуск по резерву КВ и уменьшает резерв backend-транзакцией. \
         * Статусы ВЗН для append берёт из `"stock/options.load"` → `PDT_Freor.StockInplStatesAvToAddLeaves`.
         * @param params код строки состава, резерва и количество отпуска
         * @param config опции HTTP-запроса
         */
        complete: async (params: StockGatheringCompleteReserveParams, config?: RequestConfig): Promise<void> => {
            const options = await stockOptionsServices.load(config);
            const result = await stockGatheringsApi.content.createLeaves(
                {
                    choosedReserves: [
                        {
                            LeaveDocQty: params.qty,
                            ReserveCode: params.reserveCode,
                        },
                    ],
                    gatheringContCode: params.gatheringContCode,
                    params: {
                        AppendToAnyExistingInplant: true,
                        AppendToAnyExistingInplantStates: options.PDT_Freor.StockInplStatesAvToAddLeaves,
                    },
                },
                config,
            );

            if (!result.isSucceeded) {
                throw new Error(result.errorString ?? "Не удалось отпустить резерв КВ.");
            }
        },
    },
} as const;

async function enrichContents(
    contents: ApiStockGatheringContentData[],
    include: Array<keyof StockGatheringContentExtends>,
    config?: RequestConfig,
): Promise<StockGatheringContent[]> {
    const includeStockObj = include.includes("StockObj");
    const includeMeasure = include.includes("Measure");
    const includeSenderDivision = include.includes("SenderDivisionData");
    const includeSenderSection = include.includes("SenderSectionData");
    const includeReceiverDivision = include.includes("ReceiverDivisionData");
    const includeReceiverSection = include.includes("ReceiverSectionData");
    const stockObjCodes = new Set<number>();
    const measureCodes = new Set<number>();
    const divisionCodes = new Set<number>();
    const sectionCodes = new Set<number>();

    contents.forEach((content) => {
        if (includeStockObj) addValidCode(stockObjCodes, content.StockobjCode);
        if (includeMeasure) addValidCode(measureCodes, content.MeasCode);
        if (includeSenderDivision) addValidCode(divisionCodes, content.SenderDivision);
        if (includeReceiverDivision) addValidCode(divisionCodes, content.ReceiverDivision);
        if (includeSenderSection) addValidCode(sectionCodes, content.SenderSection);
        if (includeReceiverSection) addValidCode(sectionCodes, content.ReceiverSection);
    });

    const stockObjs = new Map<number, StockObj>();
    const measures = new Map<number, ApiMeasure>();
    const divisions = new Map<number, ApiDivision>();
    const sections = new Map<number, ApiDivision>();
    const promises: Promise<unknown>[] = [];

    if (stockObjCodes.size > 0) {
        promises.push(
            stockObjsApi.load({ flt: { Codes: [...stockObjCodes] } }, config).then((items) => {
                items.forEach((item) => stockObjs.set(item.Code, serializeStockObj(item)));
            }),
        );
    }
    if (measureCodes.size > 0) {
        promises.push(
            measureApi.load({ flt: { Codes: [...measureCodes] } }, config).then((items) => {
                items.forEach((item) => measures.set(item.Code, item));
            }),
        );
    }
    if (divisionCodes.size > 0) {
        promises.push(
            divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config).then(({ items }) => {
                items.forEach((item) => divisions.set(item.Code, item));
            }),
        );
    }
    if (sectionCodes.size > 0) {
        promises.push(
            divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config).then((items) => {
                items.forEach((item) => {
                    if (item.SectionCode !== 0) {
                        sections.set(item.SectionCode, item);
                    }
                });
            }),
        );
    }

    await Promise.all(promises);

    return contents.map((content) =>
        serializeContent(content, {
            StockObj: includeStockObj ? stockObjs.get(content.StockobjCode) : undefined,
            Measure: includeMeasure ? measures.get(content.MeasCode) : undefined,
            SenderDivisionData: includeSenderDivision ? divisions.get(content.SenderDivision) : undefined,
            SenderSectionData: includeSenderSection ? sections.get(content.SenderSection) : undefined,
            ReceiverDivisionData: includeReceiverDivision ? divisions.get(content.ReceiverDivision) : undefined,
            ReceiverSectionData: includeReceiverSection ? sections.get(content.ReceiverSection) : undefined,
        }),
    );
}

async function enrichReserves(
    reserves: ApiStockReserveData[],
    include: Array<keyof StockReserveExtends>,
    config?: RequestConfig,
): Promise<StockReserve[]> {
    const includeLot = include.includes("Lot");
    const includeMeasure = include.includes("Measure");
    const includeDepotCard = include.includes("DepotCard");
    const includeRemainders = include.includes("StorePlaceRemainders");
    const includeSpMovings = include.includes("SpMovings");
    const includeOrder = include.includes("Order");
    const lotCodes = new Set<number>();
    const measureCodes = new Set<number>();
    const depotCardCodes = new Set<number>();
    const orderCodes = new Set<number>();

    reserves.forEach((reserve) => {
        if (includeLot || includeRemainders || includeSpMovings) addValidCode(lotCodes, reserve.LotCode);
        if (includeMeasure) addValidCode(measureCodes, reserve.MeasCode);
        if (includeDepotCard) addValidCode(depotCardCodes, reserve.DepotcardCode);
        if (includeOrder) addValidCode(orderCodes, reserve.OrderCode);
    });

    const lots = new Map<number, ApiStockLot>();
    const measures = new Map<number, ApiMeasure>();
    const depotCards = new Map<number, ApiStockDepotCard>();
    const orders = new Map<number, ApiOrder>();
    const remainders = new Map<number, StorePlaceRemainder[]>();
    const spMovings = new Map<number, ApiSpMovingData[]>();
    const promises: Promise<unknown>[] = [];

    if (lotCodes.size > 0 && includeLot) {
        promises.push(
            lotsServices.load({ flt: { Codes: [...lotCodes] } }, config).then((items) => {
                items.forEach((item) => lots.set(item.LotCode, item));
            }),
        );
    }
    if (measureCodes.size > 0) {
        promises.push(
            measureApi.load({ flt: { Codes: [...measureCodes] } }, config).then((items) => {
                items.forEach((item) => measures.set(item.Code, item));
            }),
        );
    }
    if (depotCardCodes.size > 0) {
        promises.push(
            stockDepotCardsApi.load({ flt: { Codes: [...depotCardCodes] } }, config).then((items) => {
                items.forEach((item) => depotCards.set(item.Code, item));
            }),
        );
    }
    if (includeOrder && orderCodes.size > 0) {
        promises.push(
            ordersApi.load({ flt: { Codes: [...orderCodes] } }, config).then((items) => {
                items.forEach((item) => orders.set(item.Code, item));
            }),
        );
    }
    if (lotCodes.size > 0 && includeRemainders) {
        promises.push(
            storePlacesServices.remainders
                .load(
                    {
                        filter: {
                            LotCodes: [...lotCodes],
                            InContextOfLot: true,
                            OnlyPositiveRemainders: true,
                        },
                        include: ["StorePlace"],
                    },
                    config,
                )
                .then((items) => {
                    items.forEach((item) => {
                        if (!isValidCode(item.LotCode)) {
                            return;
                        }
                        const lotRemainders = remainders.get(item.LotCode) ?? [];
                        lotRemainders.push(item);
                        remainders.set(item.LotCode, lotRemainders);
                    });
                }),
        );
    }
    if (lotCodes.size > 0 && includeSpMovings) {
        promises.push(
            storePlacesApi.spMoving
                .loadByFilter(
                    {
                        filter: {
                            SdbCodes: [...lotCodes],
                            OnlyNotInv: true,
                        },
                    },
                    config,
                )
                .then((movements) => {
                    movements.forEach((movement) => {
                        if (!isValidCode(movement.SdbCode)) {
                            return;
                        }

                        const lotMovements = spMovings.get(movement.SdbCode) ?? [];
                        lotMovements.push(movement);
                        spMovings.set(movement.SdbCode, lotMovements);
                    });
                }),
        );
    }

    await Promise.all(promises);

    return reserves.map((reserve) =>
        serializeReserve(reserve, {
            Lot: includeLot ? lots.get(reserve.LotCode) : undefined,
            Measure: includeMeasure ? measures.get(reserve.MeasCode) : undefined,
            DepotCard: includeDepotCard ? depotCards.get(reserve.DepotcardCode) : undefined,
            Order: includeOrder ? (orders.get(reserve.OrderCode) ?? null) : undefined,
            StorePlaceRemainders: includeRemainders ? (remainders.get(reserve.LotCode) ?? []) : undefined,
            SpMovings: includeSpMovings ? (spMovings.get(reserve.LotCode) ?? []) : undefined,
        }),
    );
}

function serializeGathering(
    apiGathering: ApiStockGatheringData,
    includeData?: Partial<StockGatheringExtends>,
): StockGathering {
    return {
        ...apiGathering,
        SenderDivision: includeData?.SenderDivision,
        SenderSectionData: includeData?.SenderSectionData,
        ReceiverDivision: includeData?.ReceiverDivision,
        ReceiverSectionData: includeData?.ReceiverSectionData,
        State: includeData?.State,
    };
}

function serializeContent(
    apiContent: ApiStockGatheringContentData,
    includeData?: Partial<StockGatheringContentExtends>,
): StockGatheringContent {
    return {
        ...apiContent,
        StockObj: includeData?.StockObj,
        Measure: includeData?.Measure,
        SenderDivisionData: includeData?.SenderDivisionData,
        SenderSectionData: includeData?.SenderSectionData,
        ReceiverDivisionData: includeData?.ReceiverDivisionData,
        ReceiverSectionData: includeData?.ReceiverSectionData,
    };
}

function serializeReserve(apiReserve: ApiStockReserveData, includeData?: Partial<StockReserveExtends>): StockReserve {
    return {
        ...apiReserve,
        Lot: includeData?.Lot,
        Measure: includeData?.Measure,
        DepotCard: includeData?.DepotCard,
        Order: includeData?.Order,
        StorePlaceRemainders: includeData?.StorePlaceRemainders,
        SpMovings: includeData?.SpMovings,
    };
}

function serializeStockObj(apiStockObj: ApiStockObj): StockObj {
    return {
        ...apiStockObj,
        KoTypeObj: undefined,
    };
}

function addValidCode(target: Set<number>, code: number): void {
    if (isValidCode(code)) {
        target.add(code);
    }
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
